import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { formatEUR, formatDate, today, parseAmount, invalidDateMessage } from "../lib/format";
import { Plus, Trash, PencilSimple, X, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import FilterBar from "../components/FilterBar";
import FilterSummary from "../components/FilterSummary";

const SEND_OPTIONS = [
  { key: "post", label: "Post" },
  { key: "fax", label: "Fax" },
  { key: "portal", label: "Portal" },
  { key: "email", label: "E-Mail" },
];

const empty = {
  customer_name: "",
  mass_start_date: today(),
  mass_end_date: "",
  payment_term_days: 20,
  ue_planned: 0,
  measure_type: "",
  has_mid_invoice: false,
  mid_amount: 0,
  mid_invoice_date: "",
  final_amount: 0,
  final_invoice_date: "",
  sent_channels: [],
  sent_date: "",
  manually_paid: false,
  paid_date: "",
  paid_amount: null,
  price_per_ue: null,
  personnel_cost_per_ue: 0,
  material_cost_flat: 0,
  room_cost_flat: 0,
  notes: "",
};

export default function AVGSPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [pricePerUe, setPricePerUe] = useState(57.87);  // aus Settings (Fallback)
  const [measureTypes, setMeasureTypes] = useState([]); // [{name, price_per_ue}]

  // Filters
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState(""); // YYYY-MM or "all"
  const [filterMonthField, setFilterMonthField] = useState("mass_start_date"); // or "final_expected_payment_date"
  const [filterStatus, setFilterStatus] = useState("all"); // all | open | paid | partial

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/avgs");
    setItems(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Settings laden für Preis pro UE
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (data?.price_per_ue) setPricePerUe(parseAmount(data.price_per_ue));
      if (Array.isArray(data?.avgs_measure_types)) setMeasureTypes(data.avgs_measure_types);
    }).catch(() => {});
  }, []);

  // Helper: Preis für einen Maßnahmen-Typ zu einem Stichtag ermitteln (Preisänderungen berücksichtigen)
  const resolveMeasurePrice = (measureType, refDate) => {
    if (!measureType) return null;
    const base = parseAmount(measureType.price_per_ue);
    const history = measureType.price_history || [];
    if (!history.length || !refDate) return base;
    let best = null;
    let bestDate = "";
    for (const p of history) {
      const pr = parseAmount(p.price_per_ue);
      if (isNaN(pr)) continue;
      if (!p.effective_from) {
        if (best === null) { best = pr; bestDate = ""; }
      } else if (String(p.effective_from) <= String(refDate) && String(p.effective_from) >= bestDate) {
        best = pr;
        bestDate = String(p.effective_from);
      }
    }
    return best !== null ? best : base;
  };

  // Aktueller Preis basierend auf gewähltem Maßnahme-Typ + Startdatum
  // Priorität: 1) manueller Override im Formular  2) Maßnahmen-Typ-Preis für mass_start_date  3) globaler Default
  const currentUePrice = () => {
    if (form.price_per_ue !== "" && form.price_per_ue !== null && form.price_per_ue !== undefined) {
      const v = parseAmount(form.price_per_ue);
      if (!isNaN(v) && v > 0) return v;
    }
    if (form.measure_type) {
      const t = measureTypes.find((m) => m.name === form.measure_type);
      const resolved = resolveMeasurePrice(t, form.mass_start_date);
      if (resolved && !isNaN(resolved)) return resolved;
    }
    return pricePerUe;
  };

  // Auto-Berechnung: UE × Preis pro UE → Endbetrag
  const handleUeChange = (val) => {
    const ue = parseAmount(val || 0);
    const total = ue * currentUePrice();
    setForm((f) => {
      const next = { ...f, ue_planned: val };
      if (f.has_mid_invoice) {
        next.mid_amount = (total / 2).toFixed(2);
        next.final_amount = (total / 2).toFixed(2);
      } else {
        next.final_amount = total.toFixed(2);
      }
      return next;
    });
  };

  const handleMeasureTypeChange = (val) => {
    setForm((f) => {
      const next = { ...f, measure_type: val };
      // Priorität: manueller Override > Maßnahmen-Typ-Preis (datumsabhängig) > globaler Default
      const overrideRaw = f.price_per_ue;
      const override = (overrideRaw !== null && overrideRaw !== "" && overrideRaw !== undefined)
        ? parseAmount(overrideRaw) : NaN;
      let price;
      if (!isNaN(override) && override > 0) {
        price = override;
      } else {
        const t = measureTypes.find((m) => m.name === val);
        const resolved = resolveMeasurePrice(t, f.mass_start_date);
        price = (resolved && !isNaN(resolved)) ? resolved : pricePerUe;
      }
      const ue = parseAmount(f.ue_planned || 0);
      const total = ue * price;
      if (ue > 0) {
        if (f.has_mid_invoice) {
          next.mid_amount = (total / 2).toFixed(2);
          next.final_amount = (total / 2).toFixed(2);
        } else {
          next.final_amount = total.toFixed(2);
        }
      }
      return next;
    });
  };

  const handleHasMidChange = (checked) => {
    setForm((f) => {
      const ue = parseAmount(f.ue_planned || 0);
      const total = ue * currentUePrice();
      const next = { ...f, has_mid_invoice: checked };
      if (checked) {
        if (!parseAmount(f.mid_amount)) next.mid_amount = (total / 2).toFixed(2);
        next.final_amount = (total - parseAmount(next.mid_amount || 0)).toFixed(2);
      } else {
        next.mid_amount = 0;
        next.mid_invoice_date = "";
        next.final_amount = total.toFixed(2);
      }
      return next;
    });
  };

  const handleMidAmountChange = (val) => {
    const mid = parseAmount(val || 0);
    const ue = parseAmount(form.ue_planned || 0);
    const total = ue * currentUePrice();
    setForm((f) => ({
      ...f,
      mid_amount: val,
      final_amount: total > 0 ? (total - mid).toFixed(2) : f.final_amount,
    }));
  };

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };

  const openDuplicate = (it) => {
    setEditing(null);
    setForm({
      customer_name: "",  // Name leeren — vom User neu einzutragen
      mass_start_date: it.mass_start_date || today(),
      mass_end_date: it.mass_end_date || "",
      payment_term_days: it.payment_term_days || 20,
      ue_planned: it.ue_planned || 0,
      has_mid_invoice: !!it.has_mid_invoice,
      mid_amount: it.mid_amount || 0,
      mid_invoice_date: it.mid_invoice_date || "",
      final_amount: it.final_amount || 0,
      final_invoice_date: it.final_invoice_date || "",
      sent_channels: [],
      sent_date: "",
      manually_paid: false,
      paid_date: "",
      notes: it.notes || "",
    });
    setOpen(true);
    toast.info("Eintrag dupliziert — bitte Teilnehmer-Name eintragen");
  };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      customer_name: it.customer_name || "",
      mass_start_date: it.mass_start_date || today(),
      mass_end_date: it.mass_end_date || "",
      payment_term_days: it.payment_term_days || 20,
      ue_planned: it.ue_planned || 0,
      measure_type: it.measure_type || "",
      has_mid_invoice: !!it.has_mid_invoice,
      mid_amount: it.mid_amount || 0,
      mid_invoice_date: it.mid_invoice_date || "",
      final_amount: it.final_amount || 0,
      final_invoice_date: it.final_invoice_date || "",
      sent_channels: it.sent_channels || [],
      sent_date: it.sent_date || "",
      manually_paid: !!it.manually_paid,
      paid_date: it.paid_date || "",
      paid_amount: it.paid_amount ?? null,
      price_per_ue: it.price_per_ue ?? null,
      personnel_cost_per_ue: it.personnel_cost_per_ue ?? 0,
      material_cost_flat: it.material_cost_flat ?? 0,
      room_cost_flat: it.room_cost_flat ?? 0,
      notes: it.notes || "",
    });
    setOpen(true);
  };

  const toggleChannel = (key) => {
    setForm((f) => {
      const has = (f.sent_channels || []).includes(key);
      const next = has ? f.sent_channels.filter((k) => k !== key) : [...(f.sent_channels || []), key];
      return { ...f, sent_channels: next };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const dateErr = invalidDateMessage(form, [["mass_start_date", "Maßnahme-Start"], ["mass_end_date", "Maßnahme-Ende"], ["mid_invoice_date", "Zwischenrechnung"], ["final_invoice_date", "Schlussrechnung"], ["sent_date", "Versanddatum"], ["paid_date", "Zahlungsdatum"]]);
    if (dateErr) { toast.error(dateErr); return; }
    try {
      const payload = {
        ...form,
        customer_name: (form.customer_name || "").replace(/\s+/g, " ").trim(),
        payment_term_days: parseInt(form.payment_term_days),
        ue_planned: parseAmount(form.ue_planned || 0),
        mid_amount: parseAmount(form.mid_amount || 0),
        final_amount: parseAmount(form.final_amount || 0),
        paid_amount: form.paid_amount === null || form.paid_amount === "" ? null : parseAmount(form.paid_amount),
        price_per_ue: (form.price_per_ue === null || form.price_per_ue === "" || form.price_per_ue === undefined) ? null : parseAmount(form.price_per_ue),
        personnel_cost_per_ue: parseAmount(form.personnel_cost_per_ue || 0),
        material_cost_flat: parseAmount(form.material_cost_flat || 0),
        room_cost_flat: parseAmount(form.room_cost_flat || 0),
      };
      if (!payload.has_mid_invoice) { payload.mid_amount = 0; payload.mid_invoice_date = ""; }
      if (editing) {
        await api.put(`/avgs/${editing}`, payload);
        toast.success("Rechnung aktualisiert");
      } else {
        await api.post("/avgs", payload);
        toast.success("Rechnung angelegt");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/avgs/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const filteredItems = items.filter((it) => {
    // Search by name
    if (search) {
      const q = search.toLowerCase().trim();
      if (!(it.customer_name || "").toLowerCase().includes(q)) return false;
    }
    // Month filter
    if (filterMonth && filterMonth !== "all") {
      const fieldVal = it[filterMonthField];
      if (!fieldVal || !String(fieldVal).startsWith(filterMonth)) return false;
    }
    // Status filter
    if (filterStatus !== "all") {
      if ((it.status || "open") !== filterStatus) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="avgs-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">AVGS</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1.5">Rechnungen nach Maßnahme</h1>
          <p className="text-sm text-[#5B6371] mt-1">
            Maßnahme-Daten eingeben — Zahlungsdatum wird automatisch berechnet (Rechnungsdatum + Zahlungsziel).
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="avgs-create-button"
          className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2.5 transition-colors"
        >
          <Plus size={16} weight="bold" /> Neue AVGS-Rechnung
        </button>
      </header>

      {/* Filterleiste */}
      <FilterBar
        items={items}
        search={search} setSearch={setSearch}
        filterMonth={filterMonth} setFilterMonth={setFilterMonth}
        filterMonthField={filterMonthField} setFilterMonthField={setFilterMonthField}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
      />

      {/* Zusammenfassung der gefilterten Ergebnisse */}
      <FilterSummary items={filteredItems} kind="avgs" />

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="avgs-table">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
                <th className="text-left px-4 py-3 font-medium">Teilnehmer</th>
                <th className="text-left px-4 py-3 font-medium">Maßnahme</th>
                <th className="text-right px-4 py-3 font-medium">Summe</th>
                <th className="text-left px-4 py-3 font-medium">Versand</th>
                <th className="text-left px-4 py-3 font-medium">Erwartet</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#94A3B8]">Lade …</td></tr>)}
              {!loading && filteredItems.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-[#94A3B8]">
                  {items.length === 0 ? "Noch keine AVGS-Rechnungen." : "Keine Treffer für die aktuellen Filter."}
                </td></tr>
              )}
              {filteredItems.map((it) => (
                <tr key={it.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`avgs-row-${it.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.customer_name}</div>
                    {it.ue_planned > 0 && <div className="text-xs text-[#94A3B8]">{it.ue_planned} UE</div>}
                  </td>
                  <td className="px-4 py-3 text-[#5B6371] text-xs">
                    {formatDate(it.mass_start_date)} – {formatDate(it.mass_end_date)}
                    <div className="text-[10px] text-[#94A3B8] mt-0.5">Zahlungsziel: {it.payment_term_days} Tage</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{formatEUR(it.total_amount)}</td>
                  <td className="px-4 py-3">
                    {(it.sent_channels && it.sent_channels.length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {it.sent_channels.map((c) => (
                          <span key={c} className="text-[10px] uppercase tracking-wider font-medium bg-[#E6F7F7] text-[#0AA09E] px-1.5 py-0.5 rounded">
                            {c === "post" ? "Post" : c === "fax" ? "Fax" : c === "portal" ? "Portal" : "Mail"}
                          </span>
                        ))}
                        {it.sent_date && (
                          <div className="w-full text-[10px] text-[#94A3B8] mt-0.5">{formatDate(it.sent_date)}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#94A3B8]">— nicht gesendet</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {it.has_mid_invoice && it.mid_expected_payment_date && (
                      <div className="text-[#5B6371]">Zwischen: {formatDate(it.mid_expected_payment_date)}</div>
                    )}
                    <div>{formatDate(it.final_expected_payment_date)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={it.status} />
                    {it.manually_paid && it.paid_date && (
                      <div className="text-[10px] text-[#94A3B8] mt-0.5">am {formatDate(it.paid_date)}</div>
                    )}
                    {it.status === "partial" && (
                      <div className="text-[10px] text-[#F59E0B] mt-0.5" data-testid={`avgs-partial-${it.id}`}>
                        {formatEUR(it.paid_amount)} erhalten · offen {formatEUR((it.total_amount || 0) - (it.paid_amount || 0))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openDuplicate(it)} className="text-[#5B6371] hover:text-[#0AA09E] p-1.5" title="Duplizieren" data-testid={`avgs-duplicate-${it.id}`}>
                      <Copy size={16} />
                    </button>
                    <button onClick={() => openEdit(it)} className="text-[#5B6371] hover:text-[#0AA09E] p-1.5" data-testid={`avgs-edit-${it.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => onDelete(it.id)} className="text-[#5B6371] hover:text-[#FF6B5C] p-1.5" data-testid={`avgs-delete-${it.id}`}>
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {filteredItems.length > 0 && (
              <tfoot>
                <tr className="bg-[#F8FAFC] border-t-2 border-[#0AA09E] font-display">
                  <td className="px-4 py-3 text-sm font-bold" colSpan={2}>
                    Summe ({filteredItems.length} {filteredItems.length === 1 ? "Rechnung" : "Rechnungen"})
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-base text-[#1F242E]">
                    {formatEUR(filteredItems.reduce((s, r) => s + (r.total_amount || 0), 0))}
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-xs text-[#5B6371]">
                    {(() => {
                      const paid = filteredItems.reduce((s, r) =>
                        s + (r.status === "partial" ? (r.paid_amount || 0) : (r.manually_paid || r.status === "paid") ? (r.total_amount || 0) : 0), 0);
                      const open = filteredItems.reduce((s, r) => s + (r.total_amount || 0), 0) - paid;
                      return (
                        <>
                          <div>Bezahlt: <strong className="text-[#0AA09E]">{formatEUR(paid)}</strong></div>
                          <div>Offen: <strong className="text-[#FF6B5C]">{formatEUR(open)}</strong></div>
                        </>
                      );
                    })()}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {open && (
        <Modal title={editing ? "AVGS-Rechnung bearbeiten" : "Neue AVGS-Rechnung"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="avgs-form">
            <Field label="Teilnehmer / Kunde">
              <input required className="field" value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                data-testid="avgs-customer-name" />
            </Field>
            <Field label="Maßnahme-Typ">
              <select className="field" value={form.measure_type}
                onChange={(e) => handleMeasureTypeChange(e.target.value)}
                data-testid="avgs-measure-type">
                <option value="">— Standard ({pricePerUe.toFixed(2)} €/UE) —</option>
                {measureTypes.map((t) => (
                  <option key={t.name} value={t.name}>{t.name} ({parseAmount(t.price_per_ue).toFixed(2)} €/UE)</option>
                ))}
              </select>
              <p className="text-[10px] text-[#94A3B8] mt-1">Du kannst Maßnahme-Typen und Preisänderungen in den Einstellungen anpassen.</p>
              {(() => {
                const t = measureTypes.find((m) => m.name === form.measure_type);
                if (!t || !(t.price_history?.length)) return null;
                const applied = resolveMeasurePrice(t, form.mass_start_date);
                return (
                  <div className="mt-2 p-2 bg-[#E6F7F7] rounded border border-[#0AA09E]/30 text-xs" data-testid="avgs-price-applied-hint">
                    <div className="text-[#0AA09E] font-medium">
                      Gültiger Preis für {form.mass_start_date || "kein Startdatum"}: <strong>{applied?.toFixed(2)} €/UE</strong>
                    </div>
                    <div className="text-[10px] text-[#5B6371] mt-1">
                      {[{ label: `Standard: ${parseAmount(t.price_per_ue).toFixed(2)} €`, effective_from: null }, ...(t.price_history || [])].map((p, i) => (
                        <span key={i} className="inline-block mr-2">
                          {p.effective_from ? `${p.label || p.effective_from}: ${parseAmount(p.price_per_ue).toFixed(2)} €` : (i === 0 ? p.label : `${p.label || "kein Datum"}: ${parseAmount(p.price_per_ue).toFixed(2)} €`)}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Maßnahme Start">
                <input type="date" min="2020-01-01" max="2100-12-31" required className="field" value={form.mass_start_date}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    setForm((f) => {
                      const next = { ...f, mass_start_date: startDate };
                      // Preis neu berechnen wenn kein Override & measure_type gesetzt
                      const overrideRaw = f.price_per_ue;
                      const hasOverride = overrideRaw !== null && overrideRaw !== "" && overrideRaw !== undefined && parseAmount(overrideRaw) > 0;
                      if (!hasOverride && f.measure_type) {
                        const t = measureTypes.find((m) => m.name === f.measure_type);
                        const price = resolveMeasurePrice(t, startDate) || pricePerUe;
                        const ue = parseAmount(f.ue_planned || 0);
                        const total = ue * price;
                        if (ue > 0) {
                          if (f.has_mid_invoice) {
                            next.mid_amount = (total / 2).toFixed(2);
                            next.final_amount = (total / 2).toFixed(2);
                          } else {
                            next.final_amount = total.toFixed(2);
                          }
                        }
                      }
                      return next;
                    });
                  }}
                  data-testid="avgs-start-date" />
              </Field>
              <Field label="Maßnahme Ende">
                <input type="date" min="2020-01-01" max="2100-12-31" required className="field" value={form.mass_end_date}
                  onChange={(e) => setForm({ ...form, mass_end_date: e.target.value })}
                  data-testid="avgs-end-date" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Zahlungsziel (Tage)">
                <select className="field" value={form.payment_term_days}
                  onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })}
                  data-testid="avgs-payment-term">
                  <option value={14}>14 Tage</option>
                  <option value={20}>20 Tage</option>
                  <option value={30}>30 Tage</option>
                  <option value={40}>40 Tage</option>
                  <option value={60}>60 Tage</option>
                </select>
              </Field>
              <Field label="UE geplant">
                <input type="text" inputMode="decimal" className="field" value={form.ue_planned}
                  onChange={(e) => handleUeChange(e.target.value)}
                  data-testid="avgs-ue" />
                <p className="text-xs text-[#0AA09E] mt-1">
                  ⓘ {form.ue_planned || 0} UE × {currentUePrice().toFixed(2)} € = <strong>{((parseAmount(form.ue_planned)||0) * currentUePrice()).toFixed(2)} €</strong>
                  {form.measure_type && <span className="text-[#94A3B8]"> (Typ: {form.measure_type})</span>}
                </p>
              </Field>
            </div>
            <Field label="Preis pro UE — Override (optional)">
              <input type="text" inputMode="decimal" className="field" value={form.price_per_ue ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => {
                    const priceOverride = v === "" ? null : parseAmount(v);
                    const price = (priceOverride && priceOverride > 0) ? priceOverride :
                                  (f.measure_type ? parseAmount((measureTypes.find(t=>t.name===f.measure_type)||{}).price_per_ue || pricePerUe) : pricePerUe);
                    const ue = parseAmount(f.ue_planned || 0);
                    const total = ue * price;
                    const next = { ...f, price_per_ue: v === "" ? null : v };
                    if (ue > 0) {
                      if (f.has_mid_invoice) {
                        next.mid_amount = (total / 2).toFixed(2);
                        next.final_amount = (total / 2).toFixed(2);
                      } else {
                        next.final_amount = total.toFixed(2);
                      }
                    }
                    return next;
                  });
                }}
                placeholder="Leer lassen für Maßnahmen-Typ / Standard"
                data-testid="avgs-price-per-ue-override" />
              <p className="text-[10px] text-[#94A3B8] mt-1">
                Falls du für diesen einen Kunden einen abweichenden Preis pro UE verwenden willst — überschreibt den Standard und den Maßnahmen-Typ-Preis.
              </p>
            </Field>

            {/* Deckungsbeitrags-Kalkulation (direkte Kosten) */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-3">Direkte Kosten (für Deckungsbeitrag)</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Personal €/UE">
                  <input type="text" inputMode="decimal" min="0" className="field" value={form.personnel_cost_per_ue}
                    onChange={(e) => setForm({ ...form, personnel_cost_per_ue: e.target.value })}
                    data-testid="avgs-personnel-cost-per-ue" placeholder="0,00" />
                </Field>
                <Field label="Material pauschal (€)">
                  <input type="text" inputMode="decimal" min="0" className="field" value={form.material_cost_flat}
                    onChange={(e) => setForm({ ...form, material_cost_flat: e.target.value })}
                    data-testid="avgs-material-cost" placeholder="0,00" />
                </Field>
                <Field label="Raum pauschal (€)">
                  <input type="text" inputMode="decimal" min="0" className="field" value={form.room_cost_flat}
                    onChange={(e) => setForm({ ...form, room_cost_flat: e.target.value })}
                    data-testid="avgs-room-cost" placeholder="0,00" />
                </Field>
              </div>
              {(() => {
                const ue = parseAmount(form.ue_planned || 0);
                const pers = parseAmount(form.personnel_cost_per_ue || 0) * ue;
                const mat = parseAmount(form.material_cost_flat || 0);
                const room = parseAmount(form.room_cost_flat || 0);
                const direct = pers + mat + room;
                const revenue = (parseAmount(form.mid_amount || 0) + parseAmount(form.final_amount || 0));
                const cm = revenue - direct;
                const cmPct = revenue > 0 ? (cm / revenue * 100) : 0;
                return (
                  <div className="mt-2 text-xs text-[#5B6371] bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-2">
                    Direkt gesamt: <strong>{direct.toFixed(2)} €</strong> · Rohdeckungsbeitrag: <strong className={cm >= 0 ? "text-[#0AA09E]" : "text-[#FF6B5C]"}>{cm.toFixed(2)} €</strong> ({cmPct.toFixed(1)} %)
                  </div>
                );
              })()}
            </div>

            <div className="border-t border-[#E5E7EB] pt-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.has_mid_invoice}
                  onChange={(e) => handleHasMidChange(e.target.checked)}
                  data-testid="avgs-has-mid" />
                Zwischenrechnung erstellen
              </label>
              {form.has_mid_invoice && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <Field label="Zwischenrechnung Datum">
                    <input type="date" min="2020-01-01" max="2100-12-31" className="field" value={form.mid_invoice_date}
                      onChange={(e) => setForm({ ...form, mid_invoice_date: e.target.value })}
                      data-testid="avgs-mid-date" />
                  </Field>
                  <Field label="Zwischenrechnung Betrag (€)">
                    <input type="text" inputMode="decimal" className="field" value={form.mid_amount}
                      onChange={(e) => handleMidAmountChange(e.target.value)}
                      data-testid="avgs-mid-amount" />
                    <p className="text-[10px] text-[#94A3B8] mt-1">Endbetrag passt sich automatisch an</p>
                  </Field>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-[#E5E7EB] pt-4">
              <Field label="Endrechnung Datum (optional)">
                <input type="date" min="2020-01-01" max="2100-12-31" className="field" value={form.final_invoice_date}
                  onChange={(e) => setForm({ ...form, final_invoice_date: e.target.value })}
                  data-testid="avgs-final-date" />
                <p className="text-xs text-[#94A3B8] mt-1">Falls leer, wird Maßnahme-Ende verwendet.</p>
              </Field>
              <Field label="Endrechnung Betrag (€)">
                <input type="text" inputMode="decimal" required className="field bg-[#F8FAFC]" value={form.final_amount}
                  onChange={(e) => setForm({ ...form, final_amount: e.target.value })}
                  data-testid="avgs-final-amount" />
                <p className="text-[10px] text-[#0AA09E] mt-1">Auto-berechnet — manuell überschreibbar</p>
              </Field>
            </div>

            <Field label="Notizen">
              <textarea className="field" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="avgs-notes" />
            </Field>

            {/* Rechnungsversand */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-2">Rechnungsversand</div>
              <div className="flex flex-wrap gap-3 mb-3">
                {SEND_OPTIONS.map((opt) => {
                  const checked = (form.sent_channels || []).includes(opt.key);
                  return (
                    <label key={opt.key} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${
                      checked ? "bg-[#E6F7F7] border-[#0AA09E] text-[#0AA09E]" : "bg-white border-[#E5E7EB] text-[#5B6371] hover:border-[#0AA09E]"
                    }`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleChannel(opt.key)}
                        data-testid={`avgs-sent-${opt.key}`} className="accent-[#0AA09E]" />
                      <span>per {opt.label}</span>
                    </label>
                  );
                })}
              </div>
              <Field label="Versanddatum">
                <input type="date" min="2020-01-01" max="2100-12-31" className="field" value={form.sent_date}
                  onChange={(e) => setForm({ ...form, sent_date: e.target.value })}
                  data-testid="avgs-sent-date" />
                <p className="text-xs text-[#0AA09E] mt-1">
                  ⓘ Falls gesetzt: das erwartete Zahlungseingangsdatum = Versanddatum + {form.payment_term_days || 20} Tage
                </p>
              </Field>
            </div>

            {/* Zahlung */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-2">Zahlung</div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.manually_paid}
                  onChange={(e) => setForm({ ...form, manually_paid: e.target.checked, paid_date: e.target.checked ? (form.paid_date || today()) : "", paid_amount: e.target.checked ? form.paid_amount : null })}
                  data-testid="avgs-manually-paid"
                  className="accent-[#0AA09E]" />
                <span className="font-medium">Rechnung wurde bezahlt</span>
              </label>
              {form.manually_paid && (() => {
                const total = ((parseAmount(form.mid_amount) || 0) + (parseAmount(form.final_amount) || 0));
                const isPartial = form.paid_amount !== null && form.paid_amount !== "" && parseAmount(form.paid_amount) > 0 && parseAmount(form.paid_amount) < total;
                return (
                  <div className="mt-3 space-y-3">
                    <div className="flex gap-3 text-sm">
                      <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                        !isPartial ? "bg-[#E6F7F7] border-[#0AA09E] text-[#0AA09E] font-medium" : "bg-white border-[#E5E7EB] text-[#5B6371]"
                      }`}>
                        <input type="radio" name="paid_mode" checked={!isPartial}
                          onChange={() => setForm({ ...form, paid_amount: null })}
                          className="accent-[#0AA09E]" data-testid="avgs-paid-full" />
                        Vollständig bezahlt ({total.toFixed(2)} €)
                      </label>
                      <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                        isPartial ? "bg-[#FFF1EE] border-[#FF6B5C] text-[#FF6B5C] font-medium" : "bg-white border-[#E5E7EB] text-[#5B6371]"
                      }`}>
                        <input type="radio" name="paid_mode" checked={isPartial}
                          onChange={() => setForm({ ...form, paid_amount: (total / 2).toFixed(2) })}
                          className="accent-[#FF6B5C]" data-testid="avgs-paid-partial" />
                        Teilbezahlt
                      </label>
                    </div>
                    {isPartial && (
                      <Field label="Bezahlter Betrag (€)">
                        <input type="text" inputMode="decimal" min="0" max={total} className="field" value={form.paid_amount}
                          onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
                          data-testid="avgs-paid-amount" />
                        <p className="text-xs text-[#FF6B5C] mt-1">
                          Offen bleibt: <strong>{(total - parseAmount(form.paid_amount || 0)).toFixed(2)} €</strong>
                        </p>
                      </Field>
                    )}
                    <Field label="Zahlungseingang am">
                      <input type="date" min="2020-01-01" max="2100-12-31" className="field" value={form.paid_date}
                        onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                        data-testid="avgs-paid-date" />
                    </Field>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm text-[#5B6371] hover:text-[#1F242E]" data-testid="avgs-cancel">
                Abbrechen
              </button>
              <button type="submit"
                className="px-5 py-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md"
                data-testid="avgs-submit">
                Speichern
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { label: "Bezahlt", cls: "bg-[#0AA09E] text-white" },
    partial: { label: "Teilweise", cls: "bg-[#E5E7EB] text-[#1F242E]" },
    open: { label: "Offen", cls: "bg-[#FFF1EE] text-[#FF6B5C]" },
  };
  const s = map[status] || map.open;
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#1F242E] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h3 className="font-display text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-[#5B6371] hover:text-[#1F242E]" data-testid="modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export { Field, Modal, StatusBadge };
